import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Building2,
  Folder,
  Folders,
  GraduationCap,
  ListEnd,
  MoreHorizontal,
  TextAlignJustify,
  User,
  Users,
} from "lucide-react";
import BadgeBoxCst from "@components/BadgeBoxCst";
import {
  ListItemCardProps,
  ListItemCardSubtitleSegment,
  ListItemCardMetrics,
  ListItemAction,
  ListItemCardDetails,
} from "./types";
import {
  CardRow,
  CheckboxArea,
  IconArea,
  InfoArea,
  TitleRow,
  MetricsRow,
  MetricItem,
  MetricClickable,
  MetricSeparator,
  MetricsWrapper,
  SubtitleRow,
  SubtitleItem,
  RightArea,
  BadgesRow,
  ActionsRow,
  ActionBtn,
  IconActionBtn,
  DetailsRow,
  DetailsWrapper,
  DetailsContainer,
  Bottom,
  Top,
} from "./style";
import { ButtonIconOnly } from "src/UI/ButtonIconOnly";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { isClassroom } from "./utils";

const ListItemCardCst: React.FC<ListItemCardProps> = ({
  icon,
  title,
  titleAdornment,
  subtitle,
  metrics,
  details,
  badges,
  actions,
  moreActions,
  selectable = false,
  selected = false,
  selectDisabled = false,
  onSelect,
  onClick,
  onMetricClick,
  isPublished,
  onPublish,
  onCreateEdition,
  activityType = null,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("list-item-card");
  const { t: tCommon } = useTranslation("common");

  const moreActionGroups: ListItemAction[][] = useMemo(() => {
    if (!moreActions || moreActions.length === 0) return [];
    const isGrouped = Array.isArray(moreActions[0]);
    return (isGrouped ? moreActions : [moreActions]) as ListItemAction[][];
  }, [moreActions]);

  const showPublishBtn = isPublished === false && !!onPublish;
  const showCreateEditionBtn = !!onCreateEdition;

  const subtitleSegments = useMemo((): ListItemCardSubtitleSegment[] => {
    if (!subtitle) return [];
    const parts: ListItemCardSubtitleSegment[] = [];
    if (subtitle.id !== undefined) parts.push({ label: `ID: ${subtitle.id}` });
    if (subtitle.activityType) parts.push({ label: subtitle.activityType });
    if (subtitle.status) parts.push({ label: subtitle.status });
    if (subtitle.mode) parts.push({ label: subtitle.mode });
    if (subtitle.email) parts.push({ label: subtitle.email });
    // TODO: translations
    if (subtitle.groupId)
      parts.push({ label: `ID Gruppo: ${subtitle.groupId}` });
    if (subtitle.segments)
      parts.push(...subtitle.segments.filter((segment) => !!segment.label));
    return parts;
  }, [subtitle]);

  const metricsRows = useMemo(() => {
    if (!metrics) return [];
    const row1: {
      key: keyof ListItemCardMetrics;
      icon: React.ReactNode;
      label: string;
    }[] = [];
    const row2: {
      key: keyof ListItemCardMetrics;
      icon: React.ReactNode;
      label: string;
    }[] = [];

    if (metrics.enrolledUsers !== undefined)
      row1.push({
        key: "enrolledUsers",
        icon: <User size={14} />,
        label: t("metrics.enrolled-users", { count: metrics.enrolledUsers }),
      });
    if (metrics.enrolledGroups !== undefined)
      row1.push({
        key: "enrolledGroups",
        icon: <Users size={14} />,
        label: t("metrics.enrolled-groups", { count: metrics.enrolledGroups }),
      });
    if (metrics.initiatives !== undefined)
      row2.push({
        key: "initiatives",
        icon: <Building2 size={14} />,
        label: t("metrics.initiatives", { count: metrics.initiatives }),
      });
    if (metrics.userInitiatives !== undefined)
      row2.push({
        key: "userInitiatives",
        icon: <Building2 size={14} />,
        label: t("metrics.user-initiatives", {
          count: metrics.userInitiatives,
        }),
      });
    if (metrics.dynamicGroups !== undefined)
      row2.push({
        key: "dynamicGroups",
        icon: <ListEnd size={14} />,
        label: t("metrics.dynamic-groups", { count: metrics.dynamicGroups }),
      });
    if (metrics.staticGroups !== undefined)
      row2.push({
        key: "staticGroups",
        icon: <TextAlignJustify size={14} />,
        label: t("metrics.static-groups", { count: metrics.staticGroups }),
      });
    if (metrics.learningObjects !== undefined)
      row2.push({
        key: "learningObjects",
        icon: <GraduationCap size={14} />,
        label: t("metrics.learning-objects", {
          count: metrics.learningObjects,
        }),
      });
    if (metrics.containedActivities !== undefined)
      row2.push({
        key: "containedActivities",
        icon: <GraduationCap size={14} />,
        label: t("metrics.contained-activities", {
          count: metrics.containedActivities,
        }),
      });
    if (metrics.courses !== undefined)
      row2.push({
        key: "courses",
        icon: <Folder size={14} />,
        label: t("metrics.courses", { count: metrics.courses }),
      });
    if (metrics.containedCourses !== undefined)
      row2.push({
        key: "containedCourses",
        icon: <Folder size={14} />,
        label: t("metrics.contained-courses", {
          count: metrics.containedCourses,
        }),
      });
    if (metrics.paths !== undefined)
      row2.push({
        key: "paths",
        icon: <Folders size={14} />,
        label: t("metrics.paths", { count: metrics.paths }),
      });

    return [row1, row2].filter((row) => row.length > 0);
  }, [metrics, t]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMoreClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (actionFn: () => void) => {
    handleMenuClose();
    actionFn();
  };

  const Details = (details: ListItemCardDetails) => {
    if (activityType && isClassroom(activityType)) {
      return (
        <>
          <DetailsRow>
            {tCommon("editionId")}: {subtitle?.sessionIdEnrolled ?? "-"}
          </DetailsRow>
          <DetailsWrapper activityType={activityType}>
            <DetailsRow>{details.row1}</DetailsRow>
            <DetailsRow>|</DetailsRow>
            <DetailsRow>{details.row2}</DetailsRow>
          </DetailsWrapper>
        </>
      );
    }
    return (
      <DetailsWrapper>
        <DetailsRow>{details.row1}</DetailsRow>
        <DetailsRow>{details.row2}</DetailsRow>
      </DetailsWrapper>
    );
  };

  return (
    <CardRow
      selected={selected}
      onClick={onClick}
      sx={{ cursor: onClick ? "pointer" : "default" }}
    >
      {selectable && (
        <>
          <CheckboxArea>
            <Checkbox
              checked={selected}
              disabled={selectDisabled}
              onChange={(e) => {
                e.stopPropagation();
                onSelect?.(!selected);
              }}
              onClick={(e) => e.stopPropagation()}
              sx={{
                color: theme.palette.divider,
                "&.Mui-checked": {
                  color: theme.palette.primary.main,
                },
                padding: "4px",
              }}
            />
          </CheckboxArea>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: theme.palette.divider }}
          />
        </>
      )}

      {icon && <IconArea>{icon}</IconArea>}

      <InfoArea>
        <Top>
          <TitleRow>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: theme.palette.text.primary,
                lineHeight: 1.3,
                overflowWrap: "anywhere",
              }}
            >
              {title}
            </Typography>
            {titleAdornment}
          </TitleRow>

          {subtitleSegments.length > 0 && (
            <SubtitleRow>
              {subtitleSegments.map((segment, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <span>|</span>}
                  <SubtitleItem>
                    {segment.icon}
                    <span>{segment.label}</span>
                  </SubtitleItem>
                </React.Fragment>
              ))}
            </SubtitleRow>
          )}

          {metricsRows.length > 0 && (
            <MetricsWrapper>
              {metricsRows.map((row, rowIndex) => (
                <MetricsRow key={rowIndex}>
                  {row.map((metric, metricIndex) => (
                    <React.Fragment key={metric.key}>
                      {metricIndex > 0 && <MetricSeparator />}
                      <MetricItem>
                        {metric.icon}
                        {onMetricClick ? (
                          <MetricClickable
                            onClick={(e) => {
                              e.stopPropagation();
                              onMetricClick(metric.key);
                            }}
                          >
                            {metric.label}
                          </MetricClickable>
                        ) : (
                          <span>{metric.label}</span>
                        )}
                      </MetricItem>
                    </React.Fragment>
                  ))}
                </MetricsRow>
              ))}
            </MetricsWrapper>
          )}
        </Top>
        <Bottom>
          {details && <DetailsContainer>{Details(details)}</DetailsContainer>}
        </Bottom>
      </InfoArea>

      <RightArea>
        <BadgesRow>
          {badges?.map((badge, index) => {
            const badgeBox = (
              <BadgeBoxCst
                variant={badge.variant}
                icon={badge.icon}
                sx={badge.sx}
              >
                {badge.label}
              </BadgeBoxCst>
            );
            return badge.tooltip ? (
              <Tooltip key={index} title={badge.tooltip}>
                <span>{badgeBox}</span>
              </Tooltip>
            ) : (
              <React.Fragment key={index}>{badgeBox}</React.Fragment>
            );
          })}
        </BadgesRow>

        {(actions ||
          moreActionGroups.length > 0 ||
          showPublishBtn ||
          showCreateEditionBtn) && (
          <ActionsRow>
            {/* TODO: da implementare quando avremo le bozze */}
            {/* {showPublishBtn && (
              <PublishBtn
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  onPublish?.();
                }}
              >
                {t("publish-button")}
              </PublishBtn>
            )} */}

            {showCreateEditionBtn && (
              <ButtonPrimary
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateEdition?.();
                }}
              >
                {t("create-edition-button")}
              </ButtonPrimary>
            )}

            {actions?.map((action, index) =>
              action.iconOnly ? (
                <IconActionBtn
                  key={index}
                  disabled={action.disabled}
                  aria-label={action.label}
                  title={action.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                >
                  {action.icon}
                </IconActionBtn>
              ) : (
                <ActionBtn
                  key={index}
                  variant={action.variant || "outlined"}
                  disabled={action.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                >
                  {action.label}
                </ActionBtn>
              ),
            )}

            {moreActionGroups.length > 0 && (
              <>
                <ButtonIconOnly
                  onClick={handleMoreClick}
                  iconOnly={<MoreHorizontal size={16} />}
                />
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  onClick={(e) => e.stopPropagation()}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  {moreActionGroups.map((group, groupIndex) => [
                    groupIndex > 0 && (
                      <Divider
                        key={`divider-${groupIndex}`}
                        sx={{ my: "4px" }}
                      />
                    ),
                    ...group.map((action, actionIndex) => (
                      <MenuItem
                        key={`${groupIndex}-${actionIndex}`}
                        disabled={action.disabled}
                        onClick={() => handleMenuAction(action.onClick)}
                        sx={{
                          fontSize: "13px",
                          color: theme.palette.text.primary,
                          gap: "10px",
                        }}
                      >
                        {action.icon && (
                          <ListItemIcon
                            sx={{
                              minWidth: "auto !important",
                              color: theme.palette.text.primary,
                            }}
                          >
                            {action.icon}
                          </ListItemIcon>
                        )}
                        {action.label}
                      </MenuItem>
                    )),
                  ])}
                </Menu>
              </>
            )}
          </ActionsRow>
        )}
      </RightArea>
    </CardRow>
  );
};

export default ListItemCardCst;
